import { normalizeAccountKey, normalizeEmail } from '@/lib/contact-vault/normalize';
import type {
  LeadOperator,
  LeadOperatorLinkConfidence,
  LeadOperatorOutreachMode,
  LeadOperatorScope,
} from '@/types/lead-operators';
import type { LeadSegment, OperatorLinkMethod } from '@/types/leads';

/** Domain root → canonical operator key + display aliases (shared brand families). */
export const OPERATOR_DOMAIN_ALIASES: Record<string, { key: string; aliases: string[] }> = {
  anewstandard: {
    key: 'new standard',
    aliases: ['new standard', '31 cannabis', 'oui d', 'ouid', 'helios', 'agrimed'],
  },
  thefirestation: {
    key: 'fire station',
    aliases: ['fire station', '906fire', 'flower plant'],
  },
  firestation: {
    key: 'fire station',
    aliases: ['fire station', '906fire'],
  },
  '906fire': {
    key: 'fire station',
    aliases: ['fire station', '906fire'],
  },
};

const LEGAL_SUFFIXES = new Set([
  'llc',
  'inc',
  'corp',
  'co',
  'ltd',
  'lp',
  'plc',
  'holdings',
  'management',
  'company',
  'dispensary',
  'cannabis',
  'marijuana',
  'the',
]);

const OWNER_SEGMENTS = new Set<LeadSegment>(['regional_chain', 'mso']);

export type OperatorLeadInput = {
  id: string;
  business_name: string;
  dba?: string | null;
  website?: string | null;
  email?: string | null;
  segment?: LeadSegment | null;
  location_count?: number | null;
  operator_id?: string | null;
  operator_link_method?: OperatorLinkMethod | null;
  city?: string | null;
  state?: string | null;
};

export type ExistingOperatorInput = Pick<
  LeadOperator,
  'id' | 'operator_key' | 'website_domain' | 'aliases' | 'display_name' | 'segment' | 'outreach_mode'
>;

export type PlannedOperatorCreate = {
  tempId: string;
  display_name: string;
  operator_key: string;
  website_domain: string | null;
  aliases: string[];
  segment: LeadSegment | null;
  outreach_mode: LeadOperatorOutreachMode;
  leadIds: string[];
};

export type PlannedOperatorAttach = {
  leadId: string;
  operatorId: string;
  confidence: LeadOperatorLinkConfidence;
  reason: string;
};

export type OperatorLinkPlan = {
  creates: PlannedOperatorCreate[];
  attaches: PlannedOperatorAttach[];
  skippedLeadIds: string[];
};

export function extractWebsiteDomain(website: string | null | undefined): string | null {
  if (!website?.trim()) return null;
  try {
    const withProtocol = /^https?:\/\//i.test(website) ? website : `https://${website}`;
    const host = new URL(withProtocol).hostname.toLowerCase().replace(/^www\./, '');
    return host || null;
  } catch {
    const cleaned = website
      .trim()
      .toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .split('/')[0];
    return cleaned || null;
  }
}

export function domainRoot(domain: string | null | undefined): string | null {
  if (!domain) return null;
  const parts = domain.split('.').filter(Boolean);
  if (parts.length < 2) return parts[0] ?? null;
  return parts[parts.length - 2] ?? null;
}

export function stripOperatorNameNoise(value: string | null | undefined): string | null {
  const key = normalizeAccountKey(value);
  if (!key) return null;
  const tokens = key.split(' ').filter((token) => token.length > 1 && !LEGAL_SUFFIXES.has(token));
  if (tokens.length === 0) return null;
  // Drop trailing single city-ish token when name is long (e.g. "fire station negaunee").
  if (tokens.length >= 3) {
    return tokens.slice(0, -1).join(' ');
  }
  return tokens.join(' ');
}

export function candidateOperatorKeys(lead: OperatorLeadInput): string[] {
  const keys = new Set<string>();
  const domain = extractWebsiteDomain(lead.website);
  const root = domainRoot(domain);
  if (root && OPERATOR_DOMAIN_ALIASES[root]) {
    keys.add(OPERATOR_DOMAIN_ALIASES[root].key);
  }
  return [...keys];
}

export function isMultiLocationUmbrellaCandidate(lead: OperatorLeadInput): boolean {
  if (lead.segment && OWNER_SEGMENTS.has(lead.segment)) return true;
  if (lead.segment === 'small_chain') return true;
  if (lead.location_count != null && lead.location_count >= 2) return true;
  return false;
}

/** Emails that appear on 2+ unlinked store cards — one contact, multiple locations. */
export function sharedContactEmailLeadIds(leads: OperatorLeadInput[]): Set<string> {
  const byEmail = new Map<string, string[]>();
  for (const lead of leads) {
    if (lead.operator_id) continue;
    const email = normalizeEmail(lead.email);
    if (!email) continue;
    const list = byEmail.get(email) ?? [];
    list.push(lead.id);
    byEmail.set(email, list);
  }
  const ids = new Set<string>();
  for (const list of byEmail.values()) {
    if (list.length < 2) continue;
    for (const id of list) ids.add(id);
  }
  return ids;
}

export function defaultOutreachMode(segment: LeadSegment | null | undefined): LeadOperatorOutreachMode {
  if (segment && OWNER_SEGMENTS.has(segment)) return 'owner_to_owner';
  return 'store_cold';
}

export function pickStrongerSegment(
  a: LeadSegment | null | undefined,
  b: LeadSegment | null | undefined
): LeadSegment | null {
  const rank: Record<LeadSegment, number> = {
    single_location: 1,
    small_chain: 2,
    regional_chain: 3,
    mso: 4,
  };
  if (!a) return b ?? null;
  if (!b) return a;
  return rank[a] >= rank[b] ? a : b;
}

/**
 * Distinct-state footprint, not a pairwise reducer (a distinct count needs the full list at
 * once). Normalizes case/whitespace before counting so e.g. 'MI' and ' mi ' don't inflate the
 * distinct count -- leads.state has no DB-level canonical-form constraint.
 */
export function deriveOperatorScope(states: Array<string | null | undefined>): LeadOperatorScope | null {
  const normalized = new Set(
    states
      .map((state) => (typeof state === 'string' ? state.trim().toUpperCase() : null))
      .filter((state): state is string => Boolean(state))
  );
  if (normalized.size === 0) return null;
  return normalized.size > 1 ? 'multi_state' : 'single_state';
}

export type PlanOperatorUmbrellaOptions = {
  /** Explicit lead IDs selected in Browse — always move these onto Operators. */
  forceLeadIds?: Iterable<string>;
};

/**
 * Pure planner: attach unlinked multi-location leads to existing operators or new clusters.
 * Singles stay unattached unless forced. Also clusters 2+ store cards that share one outreach email.
 */
export function planOperatorUmbrellaLinks(
  leads: OperatorLeadInput[],
  _existingOperators: ExistingOperatorInput[],
  options: PlanOperatorUmbrellaOptions = {}
): OperatorLinkPlan {
  const creates: PlannedOperatorCreate[] = [];
  const attaches: PlannedOperatorAttach[] = [];
  const skippedLeadIds: string[] = [];
  const forceLeadIds = new Set(options.forceLeadIds ?? []);

  for (const lead of leads) {
    if (lead.operator_id || !forceLeadIds.has(lead.id)) {
      skippedLeadIds.push(lead.id);
      continue;
    }
    const tempId = `manual-op-${lead.id}`;
    const display = lead.dba?.trim() || lead.business_name.trim() || 'Selected operator';
    creates.push({
      tempId,
      display_name: display,
      operator_key: `manual-${lead.id}`,
      website_domain: extractWebsiteDomain(lead.website),
      aliases: [],
      segment: lead.segment ?? null,
      outreach_mode: 'owner_to_owner',
      leadIds: [lead.id],
    });
    attaches.push({
      leadId: lead.id,
      operatorId: tempId,
      confidence: 'high',
      reason: 'Explicit human selection',
    });
  }

  return { creates, attaches, skippedLeadIds };
}

export function isOwnerToOwnerOperatorMode(mode: LeadOperatorOutreachMode | null | undefined): boolean {
  return mode === 'owner_to_owner';
}

/** True when this store lead belongs under Operators (linked umbrella), not Browse singles. */
export function isLinkedToOperatorUmbrella(lead: {
  operator_id?: string | null;
  operator_link_method?: OperatorLinkMethod | null;
}): boolean {
  return Boolean(
    lead.operator_id?.trim() &&
    lead.operator_link_method &&
    lead.operator_link_method !== 'legacy_unverified'
  );
}

/**
 * Browse should only show true single-store cards.
 * Multi-loc / chain segment / already-linked umbrellas belong on Operators.
 */
export function belongsOnOperatorsNotBrowse(lead: {
  operator_id?: string | null;
  operator_link_method?: OperatorLinkMethod | null;
  location_count?: number | null;
  segment?: LeadSegment | null;
}): boolean {
  if (isLinkedToOperatorUmbrella(lead)) return true;
  if (lead.location_count != null && lead.location_count >= 2) return true;
  if (lead.segment === 'small_chain' || lead.segment === 'regional_chain' || lead.segment === 'mso') {
    return true;
  }
  return false;
}

/** @deprecated Prefer isLinkedToOperatorUmbrella — any linked store leaves Track A cold Browse. */
export function isOnOwnerOperatorTrack(
  lead: { operator_id?: string | null; operator_link_method?: OperatorLinkMethod | null },
  ownerOperatorIds?: Set<string>
): boolean {
  if (!lead.operator_id) return false;
  if (!ownerOperatorIds || ownerOperatorIds.size === 0) {
    return isLinkedToOperatorUmbrella(lead);
  }
  return ownerOperatorIds.has(lead.operator_id);
}
